/**
 * DocumentosPage — antes "ContratosPage".
 *
 * Vista del módulo /documentos organizado en DOS subsecciones independientes:
 *  - "Contratos" (tipo="contrato") — documentos legales siempre vinculados a
 *    una propiedad.
 *  - "Otros" (tipo="otro") — manuales, certificados, facturas, inventario,
 *    etc. Propiedad opcional.
 *
 * La navegación entre subsecciones es por **Tabs** (no filtro toggle): cada
 * tab muestra solo los documentos de su tipo y la acción "Nuevo ..." crea
 * directamente en la subsección activa. Para el cliente (solo-lectura), las
 * tabs funcionan igual pero sin botones de edición.
 *
 * Roles:
 *  - admin / superadmin: crea, edita, elimina documentos y sube archivos.
 *    Ve todos los documentos del sistema, en ambas subsecciones.
 *  - cliente: solo-lectura — ve sus propios documentos en las dos subsecciones,
 *    puede filtrar dentro de cada una por propiedad/estado y buscar.
 *
 * El backend sigue expuesto bajo /api/contratos; el rename es solo UX.
 */

import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  DatePicker,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Tabs,
  Tag,
  Tooltip,
  Upload,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  FileExcelOutlined,
  FileWordOutlined,
  FileTextOutlined,
  FileOutlined,
  FolderOpenOutlined,
  HomeOutlined,
  InboxOutlined,
  PlusOutlined,
  SearchOutlined,
  SolutionOutlined,
  UploadOutlined,
  UserOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import Layout from "../../Components/Layout";
import PageHeader from "../../Components/PageHeader";
import { useContratosLogic } from "./useContratosLogic";
import FirmaContratoModal from "./FirmaContratoModal";
import { useFiltroPersistido } from "../../hooks/useFiltroPersistido";
import { formatPropertyName } from "../../utils/propertyDisplay";
import { normalize } from "../../utils/string.utils";
import type {
  Contrato,
  ContratoInput,
  ContratoTipo,
} from "../../services/contratos.api";
import "./DocumentosPage.css";

// ── Constantes visuales ────────────────────────────────────────────────────

const ESTADO_TAG: Record<string, { color: string; label: string }> = {
  borrador: { color: "default", label: "Borrador" },
  vigente: { color: "green", label: "Vigente" },
  vencido: { color: "red", label: "Vencido" },
};

const TIPO_LABEL: Record<ContratoTipo, string> = {
  contrato: "Contrato",
  otro: "Otro",
};

const TIPO_COLOR: Record<ContratoTipo, string> = {
  contrato: "purple",
  otro: "blue",
};

// ── Helpers ────────────────────────────────────────────────────────────────

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileIcon(mimeType: string) {
  if (mimeType === "application/pdf")
    return <FilePdfOutlined style={{ color: "#e53935" }} />;
  if (mimeType.startsWith("image/"))
    return <FileImageOutlined style={{ color: "#1890ff" }} />;
  if (mimeType.includes("spreadsheet") || mimeType.includes("excel"))
    return <FileExcelOutlined style={{ color: "#4caf50" }} />;
  if (mimeType.includes("word") || mimeType.includes("document"))
    return <FileWordOutlined style={{ color: "#2196f3" }} />;
  if (mimeType.startsWith("text/"))
    return <FileTextOutlined style={{ color: "#ff9800" }} />;
  return <FileOutlined />;
}

// ── Página ─────────────────────────────────────────────────────────────────

const DocumentosPage: React.FC = () => {
  const {
    contratos,
    loading,
    isAdmin,
    clientes,
    propiedades,
    propiedadesParaFiltro,
    sidebarOpen,
    toggleSidebar,
    handleCreate,
    handleUpdate,
    handleDelete,
    handleUploadArchivo,
    handleDeleteArchivo,
  } = useContratosLogic();

  const navigate = useNavigate();

  // ── Subsección activa (Contratos | Otros) ────────────────────────────────
  // Es el eje principal de navegación: la página NO muestra "todo mezclado".
  // El filtro por tipo desaparece — el tipo se elige con la pestaña.
  // Se recuerda por usuario admin en este navegador (round 2026-09-24, ver
  // docs/filtros-persistentes.md); el cliente no persiste nada (lo decide el
  // hook, que solo guarda para admin/superadmin).
  const [activeTab, setActiveTab] = useFiltroPersistido<ContratoTipo>(
    "documentos.pestana",
    "contrato",
    { validar: (v) => v === "contrato" || v === "otro" },
  );

  // ── Filtros internos (aplican dentro de la subsección activa) ────────────
  const [fQuery, setFQuery] = useFiltroPersistido("documentos.busqueda", "", {
    validar: (v) => typeof v === "string",
  });
  const [fProp, setFProp] = useFiltroPersistido<number | "all" | "none">(
    "documentos.propiedad",
    "all",
    {
      validar: (v) =>
        v === "all" || v === "none" || (typeof v === "number" && Number.isFinite(v)),
    },
  );
  const [fEstado, setFEstado] = useFiltroPersistido<string | "all">(
    "documentos.estado",
    "all",
    { validar: (v) => v === "all" || (typeof v === "string" && v in ESTADO_TAG) },
  );
  const [fCliente, setFCliente] = useFiltroPersistido<number | "all">(
    "documentos.cliente",
    "all",
    { validar: (v) => v === "all" || (typeof v === "number" && Number.isFinite(v)) },
  );

  // ── Modal create/edit ────────────────────────────────────────────────────
  const [modalOpen, setModalOpen] = useState(false);
  const [contratoFirma, setContratoFirma] = useState<Contrato | null>(null);
  const [editingContrato, setEditingContrato] = useState<Contrato | null>(null);
  const [saving, setSaving] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [form] = Form.useForm<ContratoInput>();
  const watchedTipo = Form.useWatch("tipo", form);

  // ── Conteo por tipo (para badges en las tabs) ────────────────────────────
  const counts = useMemo(() => {
    let contrato = 0;
    let otro = 0;
    for (const c of contratos) {
      const t: ContratoTipo = (c.tipo as ContratoTipo) ?? "contrato";
      if (t === "contrato") contrato++;
      else otro++;
    }
    return { contrato, otro };
  }, [contratos]);

  // ── Docs filtrados (SIEMPRE filtra por la subsección activa primero) ─────
  const filteredDocs = useMemo(() => {
    // normalize() pliega tildes además de mayúsculas ("penalolen" encuentra
    // "Peñalolén"), como el resto de los buscadores del ERP. Aud. 2026-07-13.
    const q = normalize(fQuery);
    return contratos.filter((c) => {
      const tipo: ContratoTipo = (c.tipo as ContratoTipo) ?? "contrato";
      if (tipo !== activeTab) return false;
      if (fEstado !== "all" && c.estado !== fEstado) return false;
      if (fCliente !== "all" && c.id_cliente !== fCliente) return false;
      if (fProp === "none" && c.id_propiedad) return false;
      if (
        fProp !== "all" &&
        fProp !== "none" &&
        c.id_propiedad !== fProp
      )
        return false;
      if (q) {
        const hay = normalize(
          `${c.titulo} ${c.descripcion ?? ""} ${
            c.propiedad?.nombrepropiedad ?? ""
          } ${c.cliente?.nombre_usuario ?? ""}`,
        );
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [contratos, activeTab, fQuery, fProp, fEstado, fCliente]);

  /** Agrupa docs por propiedad (o "Sin propiedad" al final). Orden: primero
   * las propiedades con documentos, luego el bucket "Sin propiedad". */
  const groupedDocs = useMemo(() => {
    const map = new Map<string, { label: string; docs: Contrato[] }>();
    for (const d of filteredDocs) {
      const key = d.id_propiedad ? `p-${d.id_propiedad}` : "none";
      const label = d.propiedad?.nombrepropiedad ?? "Sin propiedad";
      if (!map.has(key)) map.set(key, { label, docs: [] });
      map.get(key)!.docs.push(d);
    }
    // "none" al final siempre.
    const entries = Array.from(map.entries()).sort((a, b) => {
      if (a[0] === "none") return 1;
      if (b[0] === "none") return -1;
      return a[1].label.localeCompare(b[1].label);
    });
    return entries;
  }, [filteredDocs]);

  // ── Handlers ─────────────────────────────────────────────────────────────

  const openCreateModal = () => {
    setEditingContrato(null);
    setPendingFiles([]);
    form.resetFields();
    // El tipo por defecto sale de la subsección activa — si el admin está
    // en la tab "Contratos", la acción es "Nuevo contrato"; en "Otros"
    // pre-selecciona tipo=otro. El admin puede cambiarlo en el modal si
    // se equivocó de tab, pero el default elimina un clic.
    form.setFieldsValue({ estado: "borrador", tipo: activeTab });
    setModalOpen(true);
  };

  const openEditModal = (contrato: Contrato) => {
    setEditingContrato(contrato);
    setPendingFiles([]);
    form.setFieldsValue({
      titulo: contrato.titulo,
      descripcion: contrato.descripcion ?? undefined,
      fecha: contrato.fecha ? (dayjs(contrato.fecha) as any) : undefined,
      estado: contrato.estado,
      tipo: contrato.tipo ?? "contrato",
      id_cliente: contrato.id_cliente,
      id_propiedad: contrato.id_propiedad ?? undefined,
    });
    setModalOpen(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const payload: ContratoInput = {
        ...values,
        fecha: values.fecha
          ? dayjs(values.fecha as any).format("YYYY-MM-DD")
          : null,
        descripcion: values.descripcion || null,
        id_propiedad: values.id_propiedad || null,
      };

      let contratoId: number | null = null;
      if (editingContrato) {
        await handleUpdate(editingContrato.id, payload);
        contratoId = editingContrato.id;
      } else {
        const created = await handleCreate(payload);
        contratoId = created?.id ?? null;
      }

      if (contratoId && pendingFiles.length > 0) {
        for (const file of pendingFiles) {
          await handleUploadArchivo(contratoId, file);
        }
      }

      // Si el admin cambió el tipo en el modal (ej: abrió el "Nuevo" desde la
      // tab Contratos pero guardó como "otro"), saltamos a la tab correcta
      // para que vea el documento recién creado sin quedar perdido.
      const savedTipo: ContratoTipo =
        (values.tipo as ContratoTipo) ?? "contrato";
      if (!editingContrato && savedTipo !== activeTab) {
        setActiveTab(savedTipo);
      }

      setModalOpen(false);
      form.resetFields();
      setEditingContrato(null);
      setPendingFiles([]);
    } finally {
      setSaving(false);
    }
  };

  const handleModalCancel = () => {
    setModalOpen(false);
    form.resetFields();
    setEditingContrato(null);
    setPendingFiles([]);
  };

  // ── Helpers de render ────────────────────────────────────────────────────

  const renderDocCard = (doc: Contrato) => {
    const archivos = doc.archivos ?? [];
    const estadoTag = ESTADO_TAG[doc.estado] ?? {
      color: "default",
      label: doc.estado,
    };
    const tipo: ContratoTipo = (doc.tipo as ContratoTipo) ?? "contrato";

    return (
      <div key={doc.id} className="doc-card">
        <div className="doc-card-top">
          <div className={`doc-card-icon is-${tipo}`}>
            {tipo === "contrato" ? <SolutionOutlined /> : <FolderOpenOutlined />}
          </div>
          <div className="doc-card-title-wrap">
            <div className="doc-card-title">{doc.titulo}</div>
            <div className="doc-card-subtitle">
              {doc.fecha ? dayjs(doc.fecha).format("DD/MM/YYYY") : "Sin fecha"}
              {isAdmin && doc.cliente?.nombre_usuario ? (
                <>
                  <span>•</span>
                  <UserOutlined /> {doc.cliente.nombre_usuario}
                </>
              ) : null}
            </div>
          </div>
        </div>

        <div className="doc-card-tags">
          <Tag color={TIPO_COLOR[tipo]}>{TIPO_LABEL[tipo]}</Tag>
          <Tag color={estadoTag.color}>{estadoTag.label}</Tag>
          {doc.propiedad?.nombrepropiedad ? (
            <Tag icon={<HomeOutlined />} color="default">
              {formatPropertyName(doc.propiedad.nombrepropiedad)}
            </Tag>
          ) : null}
        </div>

        {doc.descripcion ? (
          <p className="doc-card-desc">{doc.descripcion}</p>
        ) : null}

        <div className="doc-card-files">
          <div className="doc-files-label">
            Archivos ({archivos.length})
          </div>
          {archivos.length === 0 ? (
            <div className="doc-file-empty">Sin archivos adjuntos todavía.</div>
          ) : (
            archivos.map((archivo, index) => (
              <div
                key={archivo.key}
                className="doc-file-row"
                onClick={() =>
                  navigate(`/visor/contratos/${doc.id}/${index}`)
                }
                title="Abrir documento"
              >
                <span className="doc-file-icon">
                  {fileIcon(archivo.mimeType)}
                </span>
                <span className="doc-file-name">{archivo.originalName}</span>
                <span className="doc-file-size">
                  {formatFileSize(archivo.size)}
                </span>
                {isAdmin ? (
                  <Popconfirm
                    title="¿Eliminar este archivo?"
                    onConfirm={(e) => {
                      e?.stopPropagation();
                      handleDeleteArchivo(doc.id, archivo.key);
                    }}
                    onCancel={(e) => e?.stopPropagation()}
                    okText="Eliminar"
                    cancelText="Cancelar"
                    okButtonProps={{ danger: true }}
                  >
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </Popconfirm>
                ) : null}
              </div>
            ))
          )}
        </div>

        {tipo === "contrato" && <div className="doc-card-actions"><Button size="small" type="primary" onClick={() => setContratoFirma(doc)}>{isAdmin ? "Gestionar firma" : "Ver y firmar contrato"}</Button></div>}
        {isAdmin ? (
          <div className="doc-card-actions">
            <Upload
              showUploadList={false}
              beforeUpload={(file) => {
                handleUploadArchivo(doc.id, file);
                return false;
              }}
            >
              <Button size="small" icon={<UploadOutlined />}>
                Subir archivo
              </Button>
            </Upload>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEditModal(doc)}
            >
              Editar
            </Button>
            <Popconfirm
              title="¿Eliminar este documento?"
              description="Se eliminarán también todos sus archivos."
              onConfirm={() => handleDelete(doc.id)}
              okText="Eliminar"
              cancelText="Cancelar"
              okButtonProps={{ danger: true }}
            >
              <Button size="small" danger icon={<DeleteOutlined />}>
                Eliminar
              </Button>
            </Popconfirm>
          </div>
        ) : null}
      </div>
    );
  };

  // ── Render principal ─────────────────────────────────────────────────────

  const pageTitle = isAdmin ? "Documentos" : "Mis documentos";
  const pageSubtitle = isAdmin
    ? "Subsecciones: Contratos (vinculados a una propiedad) y Otros (manuales, certificados, etc.)"
    : "Organizados en dos subsecciones: Contratos y Otros documentos de tus propiedades";

  // El botón "Nuevo ..." se adapta a la tab activa para que sea obvio qué
  // se está creando. El tipo queda pre-seleccionado en el modal (se puede
  // cambiar si el admin decide).
  const newButtonLabel =
    activeTab === "contrato" ? "Nuevo contrato" : "Nuevo documento";

  // Copy de empty-state específico a cada subsección — evita el genérico.
  const emptyDescription = (() => {
    const hasAny = counts[activeTab] > 0;
    if (!hasAny) {
      if (activeTab === "contrato") {
        return isAdmin
          ? "Aún no hay contratos en el sistema. Crea uno con el botón 'Nuevo contrato'."
          : "Todavía no tienes contratos. Cuando tu administrador suba alguno aparecerá aquí.";
      }
      return isAdmin
        ? "No hay 'otros' documentos aún. Usa esta subsección para manuales, certificados, facturas, inventarios, etc."
        : "Aún no tienes otros documentos. Aquí aparecerán manuales, certificados y documentos que no sean contratos.";
    }
    return "No hay documentos que coincidan con los filtros.";
  })();

  // Contenido que se repite dentro de cada tab (toolbar + grid). Evita
  // duplicación entre la tab "Contratos" y "Otros".
  const renderTabBody = () => (
    <>
      {/* ── Toolbar de filtros dentro de la subsección ────────────────── */}
      <div className="doc-toolbar">
        <Input
          className="doc-search"
          placeholder={
            activeTab === "contrato"
              ? "Buscar contratos por título, descripción, propiedad..."
              : "Buscar otros documentos por título, descripción..."
          }
          allowClear
          prefix={<SearchOutlined />}
          value={fQuery}
          onChange={(e) => setFQuery(e.target.value)}
        />

        <Tooltip title="Filtrar por propiedad">
          <Select
            className="doc-filter"
            value={fProp}
            onChange={(v) => setFProp(v)}
            style={{ minWidth: 200 }}
            suffixIcon={<HomeOutlined />}
            options={[
              { value: "all", label: "Todas las propiedades" },
              // "Sin propiedad" solo tiene sentido en la tab "Otros" — los
              // contratos siempre tienen propiedad.
              ...(activeTab === "otro"
                ? [{ value: "none" as const, label: "Sin propiedad" }]
                : []),
              ...propiedadesParaFiltro.map((p) => ({
                value: p.id,
                label: formatPropertyName(p.nombrepropiedad),
              })),
            ]}
          />
        </Tooltip>

        <Tooltip title="Filtrar por estado">
          <Select
            className="doc-filter"
            value={fEstado}
            onChange={(v) => setFEstado(v)}
            style={{ minWidth: 140 }}
            options={[
              { value: "all", label: "Todos los estados" },
              { value: "borrador", label: "Borrador" },
              { value: "vigente", label: "Vigente" },
              { value: "vencido", label: "Vencido" },
            ]}
          />
        </Tooltip>

        {isAdmin ? (
          <Tooltip title="Filtrar por cliente">
            <Select
              className="doc-filter"
              showSearch
              value={fCliente}
              onChange={(v) => setFCliente(v)}
              style={{ minWidth: 200 }}
              suffixIcon={<UserOutlined />}
              optionFilterProp="label"
              options={[
                { value: "all", label: "Todos los clientes" },
                ...clientes.map((c) => ({
                  value: c.id,
                  label: c.nombre_usuario,
                })),
              ]}
            />
          </Tooltip>
        ) : null}
      </div>

      {/* ── Cuerpo ──────────────────────────────────────────────────── */}
      {loading ? (
        <div className="doc-empty">
          <Empty description="Cargando documentos..." />
        </div>
      ) : filteredDocs.length === 0 ? (
        <Empty className="doc-empty" description={emptyDescription} />
      ) : (
        groupedDocs.map(([key, group]) => (
          <div key={key} className="doc-group">
            <div className="doc-group-header">
              <span className="doc-group-title">
                {key === "none" ? (
                  <FolderOpenOutlined />
                ) : (
                  <HomeOutlined />
                )}
                {key === "none" ? group.label : formatPropertyName(group.label)}
              </span>
              <span className="doc-group-count">
                {group.docs.length}{" "}
                {group.docs.length === 1 ? "documento" : "documentos"}
              </span>
            </div>
            <div className="doc-grid">{group.docs.map(renderDocCard)}</div>
          </div>
        ))
      )}
    </>
  );

  return (
    <Layout sidebarOpen={sidebarOpen} toggleSidebar={toggleSidebar}>
      <div className="doc-page">
        <PageHeader
          title={pageTitle}
          subtitle={pageSubtitle}
          extra={
            isAdmin ? (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={openCreateModal}
              >
                {newButtonLabel}
              </Button>
            ) : undefined
          }
        />

        {!isAdmin ? (
          <Alert
            style={{ marginBottom: 12 }}
            type="info"
            showIcon
            message="Aquí puedes consultar tus documentos y firmar los contratos que te envíe la administración."
          />
        ) : null}

        {/* ── Subsecciones Contratos / Otros ─────────────────────────── */}
        <Tabs
          activeKey={activeTab}
          onChange={(k) => {
            setActiveTab(k as ContratoTipo);
            // Resetear filtros al cambiar de subsección — evita arrastrar un
            // filtro por propiedad "sin propiedad" de Otros a Contratos, que
            // daría vacío sin que el usuario entienda por qué.
            setFProp("all");
            setFEstado("all");
            setFQuery("");
          }}
          size="large"
          items={[
            {
              key: "contrato",
              label: (
                <span>
                  <SolutionOutlined /> Contratos{" "}
                  <Badge
                    count={counts.contrato}
                    showZero
                    style={{
                      backgroundColor:
                        activeTab === "contrato" ? "#7c3aed" : "#d1d5db",
                      color: activeTab === "contrato" ? "#fff" : "#374151",
                      marginLeft: 4,
                    }}
                  />
                </span>
              ),
              children: renderTabBody(),
            },
            {
              key: "otro",
              label: (
                <span>
                  <FolderOpenOutlined /> Otros{" "}
                  <Badge
                    count={counts.otro}
                    showZero
                    style={{
                      backgroundColor:
                        activeTab === "otro" ? "#2563eb" : "#d1d5db",
                      color: activeTab === "otro" ? "#fff" : "#374151",
                      marginLeft: 4,
                    }}
                  />
                </span>
              ),
              children: renderTabBody(),
            },
          ]}
        />
      </div>

      {/* ── Create / Edit Modal ──────────────────────────────────────── */}
      {contratoFirma && <FirmaContratoModal doc={contratoFirma} isAdmin={isAdmin} onClose={() => setContratoFirma(null)} />}
      <Modal
        title={editingContrato ? "Editar documento" : "Nuevo documento"}
        open={modalOpen}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        confirmLoading={saving}
        okText={editingContrato ? "Guardar" : "Crear"}
        cancelText="Cancelar"
        destroyOnClose
        width={560}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ estado: "borrador", tipo: "contrato" }}
        >
          <Form.Item
            name="tipo"
            label="Tipo de documento"
            rules={[{ required: true, message: "El tipo es requerido" }]}
            tooltip={
              "Un contrato siempre va asociado a una propiedad. Si el documento no es un contrato, elige 'Otro' (manuales, certificados, etc.) y la propiedad será opcional."
            }
          >
            <Segmented
              block
              options={[
                {
                  label: "Contrato",
                  value: "contrato",
                  icon: <SolutionOutlined />,
                },
                {
                  label: "Otro",
                  value: "otro",
                  icon: <FolderOpenOutlined />,
                },
              ]}
            />
          </Form.Item>

          <Form.Item
            name="titulo"
            label="Título"
            rules={[{ required: true, message: "El título es requerido" }]}
          >
            <Input placeholder="Ej: Contrato de arriendo 2026-04" />
          </Form.Item>

          <Form.Item name="descripcion" label="Descripción">
            <Input.TextArea
              rows={3}
              placeholder="Descripción opcional (visible al cliente)"
            />
          </Form.Item>

          <Space.Compact block>
            <Form.Item
              name="fecha"
              label="Fecha"
              style={{ flex: 1, marginRight: 8 }}
            >
              <DatePicker
                format="DD/MM/YYYY"
                style={{ width: "100%" }}
                placeholder="Fecha del documento"
              />
            </Form.Item>
            <Form.Item
              name="estado"
              label="Estado"
              style={{ flex: 1 }}
              rules={[{ required: true, message: "El estado es requerido" }]}
            >
              <Select>
                <Select.Option value="borrador">Borrador</Select.Option>
                <Select.Option value="vigente">Vigente</Select.Option>
                <Select.Option value="vencido">Vencido</Select.Option>
              </Select>
            </Form.Item>
          </Space.Compact>

          <Form.Item
            name="id_cliente"
            label="Cliente"
            rules={[{ required: true, message: "El cliente es requerido" }]}
          >
            <Select
              showSearch
              placeholder="Seleccionar cliente"
              optionFilterProp="label"
              options={clientes.map((c) => ({
                value: c.id,
                label: c.nombre_usuario,
              }))}
            />
          </Form.Item>

          <Form.Item
            name="id_propiedad"
            label="Propiedad"
            rules={
              watchedTipo === "contrato"
                ? [
                    {
                      required: true,
                      message:
                        "Un contrato debe estar vinculado a una propiedad",
                    },
                  ]
                : []
            }
            tooltip={
              watchedTipo === "contrato"
                ? "Requerido para documentos de tipo contrato."
                : "Opcional para documentos de tipo 'otro'."
            }
          >
            <Select
              showSearch
              allowClear={watchedTipo !== "contrato"}
              placeholder={
                watchedTipo === "contrato"
                  ? "Seleccionar propiedad"
                  : "Seleccionar propiedad (opcional)"
              }
              optionFilterProp="label"
              options={propiedades.map((p) => ({
                value: p.id,
                label: formatPropertyName(p.nombrepropiedad),
              }))}
            />
          </Form.Item>

          <Form.Item label="Archivos">
            <Upload.Dragger
              multiple
              beforeUpload={(file) => {
                setPendingFiles((prev) => [...prev, file]);
                return false;
              }}
              onRemove={(file) => {
                setPendingFiles((prev) =>
                  prev.filter((f) => f.name !== file.name),
                );
              }}
              fileList={pendingFiles.map((f, i) => ({
                uid: `pending-${i}`,
                name: f.name,
                status: "done" as const,
              }))}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                Arrastra archivos aquí o haz clic para adjuntar
              </p>
              <p className="ant-upload-hint" style={{ fontSize: 12 }}>
                PDF, imágenes, Word, Excel — hasta 25 MB por archivo.
              </p>
            </Upload.Dragger>
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
};

export default DocumentosPage;
